import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { getPostById } from '@/mocks/boardDetailData';
import { boardPosts } from '@/mocks/searchData';
import { useAuth } from '@/hooks/useAuth';
import supabase from '@/lib/supabase';

interface Comment {
  id: number;
  post_id: string;
  author: string;
  content: string;
  created_at: string;
}

interface SupabasePost {
  id: number;
  title: string;
  content: string;
  author: string;
  category: string;
  views: number;
  created_at: string;
  user_id: string | null;
  image_url: string | null;
}

export default function BoardDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const loginRedirect = `?redirect=${encodeURIComponent(location.pathname)}`;
  const post = id ? getPostById(id) : undefined;
  const summary = id ? boardPosts.find((p) => String(p.id) === id) : undefined;

  const [commentText, setCommentText] = useState('');
  const [comments, setComments] = useState<Comment[]>([]);
  const [loadingComments, setLoadingComments] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [commentError, setCommentError] = useState('');
  const [commentSuccess, setCommentSuccess] = useState('');

  const [supabasePost, setSupabasePost] = useState<SupabasePost | null>(null);
  const [loadingPost, setLoadingPost] = useState(true);

  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [likeLoading, setLikeLoading] = useState(false);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const [showLikeLoginPrompt, setShowLikeLoginPrompt] = useState(false);
  const [showReportLoginPrompt, setShowReportLoginPrompt] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetail, setReportDetail] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportError, setReportError] = useState('');
  const [reportSuccess, setReportSuccess] = useState('');

  const reportReasons = [
    '스팸/광고',
    '혐오 발언',
    '개인정보 노출',
    '허위 정보',
    '기타',
  ];

  const isOwner = user && supabasePost?.user_id && user.id === supabasePost.user_id;

  useEffect(() => {
    if (!id) return;
    fetchPostAndIncrementView();
    fetchComments();
  }, [id]);

  useEffect(() => {
    if (!id || !user) return;
    checkLikeStatus();
  }, [id, user]);

  useEffect(() => {
    if (!id) return;
    fetchLikeCount();
  }, [id]);

  const fetchPostAndIncrementView = async () => {
    setLoadingPost(true);
    try {
      const numericId = parseInt(id!, 10);

      const { data: postData, error: postError } = await supabase
        .from('posts')
        .select('*')
        .eq('id', numericId)
        .maybeSingle();

      if (postError) throw postError;

      if (postData) {
        setSupabasePost(postData as SupabasePost);
      }

      try {
        await supabase.rpc('increment_post_views', { post_id_param: numericId });
      } catch {
        // view increment is best-effort, silently ignore
      }
    } catch {
      // fall back to mock data
    } finally {
      setLoadingPost(false);
    }
  };

  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const { data, error } = await supabase
        .from('comments')
        .select('*')
        .eq('post_id', id!)
        .order('created_at', { ascending: true });

      if (error) throw error;

      if (data && data.length > 0) {
        setComments(data as Comment[]);
      } else if (post?.comments) {
        setComments(post.comments.map((c) => ({
          id: c.id as number,
          post_id: id!,
          author: typeof c.author === 'string' ? c.author : '익명',
          content: typeof c.content === 'string' ? c.content : '',
          created_at: new Date().toISOString(),
        })));
      }
    } catch {
      if (post?.comments) {
        setComments(post.comments.map((c) => ({
          id: c.id as number,
          post_id: id!,
          author: typeof c.author === 'string' ? c.author : '익명',
          content: typeof c.content === 'string' ? c.content : '',
          created_at: new Date().toISOString(),
        })));
      }
    } finally {
      setLoadingComments(false);
    }
  };

  const fetchLikeCount = async () => {
    try {
      const numericId = parseInt(id!, 10);
      const { count, error } = await supabase
        .from('post_likes')
        .select('*', { count: 'exact', head: true })
        .eq('post_id', numericId);

      if (!error && count !== null) {
        setLikeCount(count);
      }
    } catch {
      // silently fail
    }
  };

  const checkLikeStatus = async () => {
    if (!user || !id) return;
    try {
      const numericId = parseInt(id, 10);
      const { data, error } = await supabase
        .from('post_likes')
        .select('id')
        .eq('post_id', numericId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (!error && data) {
        setLiked(true);
      }
    } catch {
      // silently fail
    }
  };

  const handleLikeToggle = async () => {
    if (!user) {
      setShowLikeLoginPrompt(true);
      return;
    }

    setLikeLoading(true);
    try {
      const numericId = parseInt(id!, 10);

      if (liked) {
        const { error } = await supabase
          .from('post_likes')
          .delete()
          .eq('post_id', numericId)
          .eq('user_id', user.id);

        if (error) throw error;
        setLiked(false);
        setLikeCount((prev) => Math.max(0, prev - 1));
      } else {
        const { error } = await supabase
          .from('post_likes')
          .insert({ post_id: numericId, user_id: user.id });

        if (error) throw error;
        setLiked(true);
        setLikeCount((prev) => prev + 1);
      }
    } catch {
      // silently fail
    } finally {
      setLikeLoading(false);
    }
  };

  const handleReportClick = () => {
    if (!user) {
      setShowReportLoginPrompt(true);
      return;
    }
    setReportReason('');
    setReportDetail('');
    setReportError('');
    setReportSuccess('');
    setShowReportModal(true);
  };

  const handleReportSubmit = async () => {
    if (!reportReason || !user || !id) return;

    setReportSubmitting(true);
    setReportError('');

    try {
      const numericId = parseInt(id, 10);
      const { error } = await supabase
        .from('reports')
        .insert({
          post_id: numericId,
          reporter_id: user.id,
          reason: reportReason,
          detail: reportDetail.trim() || null,
        });

      if (error) {
        if (error.code === '23505') {
          setReportError('이미 이 게시글을 신고하셨습니다.');
        } else {
          setReportError(error.message || '신고 접수 중 오류가 발생했습니다.');
        }
        return;
      }

      setReportSuccess('신고가 접수되었습니다. 검토 후 적절히 조치하겠습니다.');
      setTimeout(() => {
        setShowReportModal(false);
        setReportSuccess('');
        setReportReason('');
        setReportDetail('');
      }, 2000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '알 수 없는 오류';
      setReportError(message || '신고 접수 중 오류가 발생했습니다.');
    } finally {
      setReportSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!id || !isOwner) return;
    setDeleting(true);
    setDeleteError('');

    try {
      const numericId = parseInt(id, 10);
      const { error } = await supabase
        .from('posts')
        .delete()
        .eq('id', numericId)
        .eq('user_id', user!.id);

      if (error) throw error;

      navigate('/board');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '알 수 없는 오류';
      setDeleteError(message || '삭제 중 오류가 발생했습니다.');
      setDeleteConfirmOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  const displayTitle = supabasePost?.title || summary?.title;
  const displayAuthor = supabasePost?.author || summary?.author;
  const displayCategory = supabasePost?.category || summary?.category;
  const displayViews = supabasePost ? (supabasePost.views + 1) : (summary?.views || 0);
  const displayDate = supabasePost?.created_at
    ? formatDateOnly(supabasePost.created_at)
    : (summary?.date || summary?.time || '');
  const displayContent = supabasePost?.content || post?.content || `${summary?.title || '게시글'}에 대한 상세 내용입니다. 자세한 내용은 곧 업데이트될 예정입니다.`;

  if (!post && !summary && !supabasePost && !loadingPost) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 bg-foreground-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-file-unknow-line text-2xl text-foreground-400" />
            </div>
            <h2 className="text-lg font-bold text-foreground-950 mb-2">게시글을 찾을 수 없습니다</h2>
            <p className="text-sm text-foreground-500 mb-6">삭제되었거나 존재하지 않는 게시글입니다.</p>
            <Link to="/board" className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-5 py-2.5 rounded-md transition-colors whitespace-nowrap">
              <i className="ri-arrow-left-line" />
              목록으로 돌아가기
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const categoryColor =
    displayCategory === '피해사례' ? 'bg-red-50 text-red-600' :
    displayCategory === '정보공유' ? 'bg-green-50 text-green-600' :
    displayCategory === '검증요청' ? 'bg-yellow-50 text-yellow-600' :
    displayCategory === '질문' ? 'bg-blue-50 text-blue-600' :
    'bg-secondary-100 text-secondary-600';

  const handleSubmitComment = async () => {
    if (!commentText.trim() || !id) return;

    setCommentError('');
    setCommentSuccess('');
    setSubmitting(true);

    try {
      const { data, error } = await supabase
        .from('comments')
        .insert({
          post_id: id,
          author: '익명',
          content: commentText.trim(),
        })
        .select()
        .single();

      if (error) throw error;

      if (data) {
        setComments((prev) => [...prev, data as Comment]);
        setCommentText('');
        setCommentSuccess('댓글이 등록되었습니다.');
        setTimeout(() => setCommentSuccess(''), 3000);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : '알 수 없는 오류';
      setCommentError(message || '댓글 등록 중 오류가 발생했습니다. 다시 시도해주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Header />

      <main className="flex-1">
        <div className="bg-secondary-950">
          <div className="max-w-3xl mx-auto px-4 md:px-6 py-10 md:py-14">
            {loadingPost ? (
              <div className="flex items-center gap-3">
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span className="text-foreground-400 text-sm">게시글을 불러오는 중...</span>
              </div>
            ) : (
              <>
                <div className="flex items-start justify-between gap-4">
                  <h1 className="text-xl md:text-2xl font-bold text-white mb-3">{displayTitle}</h1>
                </div>
                <div className="flex items-center gap-3 text-xs text-foreground-400 flex-wrap">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${categoryColor}`}>
                    {displayCategory}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line" />
                    {displayAuthor}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-time-line" />
                    {displayDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line" />
                    {displayViews.toLocaleString()}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-chat-3-line" />
                    {comments.length}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-thumb-up-line" />
                    {likeCount}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
          <div className="bg-white rounded-lg border border-foreground-200 p-6 md:p-8 mb-8">
            <div className="text-sm text-foreground-800 leading-relaxed whitespace-pre-wrap">
              {displayContent}
            </div>

            {supabasePost?.image_url && (() => {
              try {
                const urls = JSON.parse(supabasePost.image_url);
                if (Array.isArray(urls) && urls.length > 0) {
                  return (
                    <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
                      {urls.filter((u: string) => u).map((url: string, i: number) => (
                        <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="block">
                          <div className="aspect-square rounded-lg overflow-hidden bg-background-50">
                            <img src={url} alt={`첨부 이미지 ${i + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform" />
                          </div>
                        </a>
                      ))}
                    </div>
                  );
                }
              } catch {
                // invalid JSON
              }
              return null;
            })()}

            <div className="flex items-center justify-between mt-8 pt-6 border-t border-foreground-200">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleLikeToggle}
                  disabled={likeLoading}
                  className={`flex items-center gap-1.5 text-sm transition-colors whitespace-nowrap ${
                    liked
                      ? 'text-primary-500 font-semibold'
                      : 'text-foreground-500 hover:text-primary-500'
                  }`}
                >
                  <i className={liked ? 'ri-thumb-up-fill' : 'ri-thumb-up-line'} />
                  {liked ? '추천됨' : '추천'} {likeCount > 0 && likeCount}
                </button>
                <button
                  onClick={handleReportClick}
                  className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-red-500 transition-colors whitespace-nowrap"
                >
                  <i className="ri-alert-line" /> 신고
                </button>
              </div>
              <div className="flex items-center gap-2">
                {isOwner && (
                  <>
                    <Link
                      to={`/board/edit/${id}`}
                      className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-500 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-edit-line" /> 수정
                    </Link>
                    <button
                      onClick={() => setDeleteConfirmOpen(true)}
                      className="flex items-center gap-1 text-sm text-foreground-500 hover:text-red-500 transition-colors whitespace-nowrap"
                    >
                      <i className="ri-delete-bin-line" /> 삭제
                    </button>
                  </>
                )}
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href).then(() => {
                      setShareCopied(true);
                      setTimeout(() => setShareCopied(false), 2000);
                    }).catch(() => {});
                  }}
                  className="flex items-center gap-1 text-sm text-foreground-500 hover:text-foreground-700 transition-colors whitespace-nowrap"
                >
                  <i className={shareCopied ? 'ri-check-line' : 'ri-share-forward-line'} />
                  {shareCopied ? '복사됨' : '공유'}
                </button>
              </div>
            </div>

            {showLikeLoginPrompt && (
              <div className="mt-4 p-4 bg-background-50 border border-foreground-200 rounded-lg flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
                    <i className="ri-thumb-up-line text-primary-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground-900">추천은 로그인 후 이용 가능합니다</p>
                    <p className="text-xs text-foreground-500 mt-0.5">로그인하고 마음에 드는 게시글을 추천해보세요</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => setShowLikeLoginPrompt(false)}
                    className="text-xs text-foreground-500 hover:text-foreground-700 transition-colors whitespace-nowrap px-2 py-1"
                  >
                    닫기
                  </button>
                  <Link
                    to={`/login${loginRedirect}`}
                    className="inline-flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors whitespace-nowrap"
                  >
                    <i className="ri-login-box-line" />
                    로그인하기
                  </Link>
                </div>
              </div>
            )}

            {showReportLoginPrompt && (
              <div className="mt-3 p-4 bg-red-50/50 border border-red-200 rounded-lg flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-alert-line text-red-500" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground-900">신고는 로그인 후 이용 가능합니다</p>
                    <p className="text-xs text-foreground-500 mt-0.5">로그인하고 부적절한 게시글을 신고해주세요</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => setShowReportLoginPrompt(false)}
                    className="text-xs text-foreground-500 hover:text-foreground-700 transition-colors whitespace-nowrap px-2 py-1"
                  >
                    닫기
                  </button>
                  <Link
                    to={`/login${loginRedirect}`}
                    className="inline-flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-4 py-2 rounded-md transition-colors whitespace-nowrap"
                  >
                    <i className="ri-login-box-line" />
                    로그인하기
                  </Link>
                </div>
              </div>
            )}
          </div>

          <div className="mb-8">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-sm font-bold text-foreground-950">
                댓글 <span className="text-primary-500">{comments.length}</span>
              </h3>
            </div>

            {loadingComments ? (
              <div className="text-center py-12 bg-white rounded-lg border border-foreground-200">
                <div className="w-6 h-6 border-2 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                <p className="text-sm text-foreground-500">댓글을 불러오는 중...</p>
              </div>
            ) : comments.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-lg border border-foreground-200">
                <i className="ri-chat-3-line text-3xl text-foreground-300 block mb-3" />
                <p className="text-sm text-foreground-500">아직 댓글이 없습니다. 첫 댓글을 작성해보세요!</p>
              </div>
            ) : (
              <div className="space-y-0">
                {comments.map((comment, idx) => (
                  <div
                    key={comment.id}
                    className={`bg-white border border-foreground-200 p-4 ${
                      idx === 0 ? 'rounded-t-lg' : ''
                    } ${idx === comments.length - 1 ? 'rounded-b-lg border-b' : 'border-b-0'}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground-950">{comment.author}</span>
                        {comment.author === displayAuthor && (
                          <span className="px-1.5 py-0.5 rounded text-xs bg-primary-50 text-primary-600 font-medium">글쓴이</span>
                        )}
                      </div>
                      <span className="text-xs text-foreground-400">{formatRelativeTime(comment.created_at)}</span>
                    </div>
                    <p className="text-sm text-foreground-700 leading-relaxed">{comment.content}</p>
                    <button className="mt-2 text-xs text-foreground-400 hover:text-foreground-600 transition-colors">
                      답글
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="bg-white rounded-lg border border-foreground-200 p-4 mt-4">
              {user ? (
                <>
                  <textarea
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="댓글을 입력해주세요"
                    maxLength={500}
                    rows={3}
                    className="w-full text-sm text-foreground-950 bg-background-50 border border-foreground-200 rounded-md px-4 py-3 resize-none focus:outline-none focus:border-primary-500 placeholder:text-foreground-400"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs text-foreground-400">{commentText.length}/500</span>
                    <button
                      onClick={handleSubmitComment}
                      disabled={!commentText.trim() || submitting}
                      className="bg-primary-500 hover:bg-primary-600 disabled:bg-foreground-200 disabled:text-foreground-400 disabled:cursor-not-allowed text-white text-sm font-medium px-5 py-2 rounded-md transition-colors whitespace-nowrap"
                    >
                      {submitting ? '등록 중...' : '등록'}
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-6">
                  <i className="ri-lock-line text-2xl text-foreground-300 block mb-2" />
                  <p className="text-sm text-foreground-500 mb-3">댓글을 작성하려면 로그인이 필요합니다</p>
                  <Link
                    to={`/login${loginRedirect}`}
                    className="inline-flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium px-5 py-2 rounded-md transition-colors whitespace-nowrap"
                  >
                    <i className="ri-login-box-line" />
                    로그인하고 댓글 작성하기
                  </Link>
                </div>
              )}
              {commentError && (
                <p className="text-red-500 text-xs mt-2">{commentError}</p>
              )}
              {commentSuccess && (
                <p className="text-green-500 text-xs mt-2">{commentSuccess}</p>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between">
            <Link
              to="/board"
              className="inline-flex items-center gap-2 bg-background-100 hover:bg-background-200 text-foreground-700 text-sm font-medium px-5 py-2.5 rounded-md transition-colors whitespace-nowrap"
            >
              <i className="ri-list-check" />
              목록
            </Link>
            <div className="flex items-center gap-2">
              <Link
                to={`/board/${Math.max(1, Number(id || 1) - 1)}`}
                className="inline-flex items-center gap-1 bg-white border border-foreground-200 hover:bg-background-50 text-foreground-700 text-sm font-medium px-4 py-2.5 rounded-md transition-colors whitespace-nowrap"
              >
                <i className="ri-arrow-left-s-line" /> 이전글
              </Link>
              <Link
                to={`/board/${Number(id || 1) + 1}`}
                className="inline-flex items-center gap-1 bg-white border border-foreground-200 hover:bg-background-50 text-foreground-700 text-sm font-medium px-4 py-2.5 rounded-md transition-colors whitespace-nowrap"
              >
                다음글 <i className="ri-arrow-right-s-line" />
              </Link>
            </div>
          </div>
        </div>
      </main>

      {showReportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => !reportSubmitting && setShowReportModal(false)} />
          <div className="relative bg-white rounded-lg p-6 shadow-lg max-w-md w-full mx-4">
            <h3 className="text-base font-bold text-foreground-950 mb-1">게시글 신고</h3>
            <p className="text-sm text-foreground-500 mb-4">신고 사유를 선택해주세요. 검토 후 적절히 조치하겠습니다.</p>

            {reportSuccess ? (
              <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg mb-4">
                <i className="ri-checkbox-circle-line text-green-500 text-lg" />
                <p className="text-sm text-green-700">{reportSuccess}</p>
              </div>
            ) : (
              <>
                <div className="space-y-2 mb-4">
                  {reportReasons.map((reason) => (
                    <button
                      key={reason}
                      onClick={() => setReportReason(reason)}
                      className={`w-full text-left px-4 py-2.5 rounded-md border text-sm transition-colors whitespace-nowrap ${
                        reportReason === reason
                          ? 'border-red-400 bg-red-50 text-red-700 font-semibold'
                          : 'border-foreground-200 text-foreground-700 hover:bg-background-50'
                      }`}
                    >
                      {reason}
                    </button>
                  ))}
                </div>

                <textarea
                  value={reportDetail}
                  onChange={(e) => setReportDetail(e.target.value)}
                  placeholder="추가 설명이 있다면 입력해주세요 (선택사항)"
                  maxLength={500}
                  rows={3}
                  className="w-full text-sm text-foreground-950 bg-background-50 border border-foreground-200 rounded-md px-4 py-3 resize-none focus:outline-none focus:border-red-400 placeholder:text-foreground-400 mb-1"
                />
                <p className="text-xs text-foreground-400 mb-4">{reportDetail.length}/500</p>

                {reportError && (
                  <p className="text-sm text-red-500 mb-3 flex items-center gap-1">
                    <i className="ri-error-warning-line" /> {reportError}
                  </p>
                )}

                <div className="flex items-center gap-2 justify-end">
                  <button
                    onClick={() => setShowReportModal(false)}
                    disabled={reportSubmitting}
                    className="px-4 py-2 text-sm font-medium text-foreground-700 bg-background-100 hover:bg-background-200 rounded-md transition-colors whitespace-nowrap"
                  >
                    취소
                  </button>
                  <button
                    onClick={handleReportSubmit}
                    disabled={!reportReason || reportSubmitting}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {reportSubmitting ? '접수 중...' : '신고하기'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {deleteConfirmOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteConfirmOpen(false)} />
          <div className="relative bg-white rounded-lg p-6 shadow-lg max-w-sm w-full mx-4 animate-in fade-in zoom-in">
            <h3 className="text-base font-bold text-foreground-950 mb-2">게시글 삭제</h3>
            <p className="text-sm text-foreground-600 mb-1">정말로 이 게시글을 삭제하시겠습니까?</p>
            <p className="text-xs text-red-500 mb-5">삭제된 게시글은 복구할 수 없습니다.</p>
            {deleteError && (
              <p className="text-sm text-red-500 mb-3 flex items-center gap-1">
                <i className="ri-error-warning-line" /> {deleteError}
              </p>
            )}
            <div className="flex items-center gap-2 justify-end">
              <button
                onClick={() => setDeleteConfirmOpen(false)}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-foreground-700 bg-background-100 hover:bg-background-200 rounded-md transition-colors whitespace-nowrap"
              >
                취소
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors whitespace-nowrap disabled:opacity-50"
              >
                {deleting ? '삭제 중...' : '삭제하기'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

function formatDateOnly(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}.${m}.${day}`;
  } catch {
    return dateStr;
  }
}

function formatRelativeTime(dateStr: string): string {
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMin = Math.floor(diffMs / 60000);
    const diffHour = Math.floor(diffMs / 3600000);
    const diffDay = Math.floor(diffMs / 86400000);

    if (diffMin < 1) return '방금 전';
    if (diffMin < 60) return `${diffMin}분 전`;
    if (diffHour < 24) return `${diffHour}시간 전`;
    if (diffDay < 7) return `${diffDay}일 전`;

    return formatDateOnly(dateStr);
  } catch {
    return dateStr;
  }
}